import clsx from "clsx"

import { useProps } from "contexts/PropsContext"

/**
 * A text input for a money value.
 *
 * 🔴 `type="text"`, NEVER `type="number"`. A number input hands back a JS number
 * (or a browser-normalised string), and $TOKEN carries 18 decimal places — enough
 * to be silently rounded on the way in. It also introduces spinners and locale
 * decimal separators on a field where "1,5" and "1.5" must not be interchangeable.
 * Validation is an explicit regex on the string instead.
 */
export const TokenField = ({
  label,
  value,
  onChange,
  placeholder,
  hint,
  invalid,
  readOnly,
  suffix
}: {
  label: string
  value: string
  onChange?: (v: string) => void
  placeholder?: string
  hint?: string
  invalid?: boolean
  readOnly?: boolean
  suffix?: string
}) => {
  const { darkMode } = useProps()

  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label}</label>
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          value={value}
          readOnly={readOnly}
          onChange={(ev) => onChange?.(ev.target.value)}
          placeholder={placeholder}
          className={clsx(
            "w-full rounded-lg border px-3 py-2 font-mono text-sm outline-none transition-colors",
            suffix && "pr-16",
            darkMode ? "bg-[#060715] text-white" : "bg-white text-gray-900",
            invalid ? "border-red-500" : darkMode ? "border-[rgba(255,255,255,0.1)]" : "border-gray-300",
            readOnly && "cursor-not-allowed opacity-60"
          )}
        />
        {suffix && <span className={clsx("absolute right-3 top-1/2 -translate-y-1/2 text-xs", darkMode ? "text-gray-500" : "text-gray-400")}>{suffix}</span>}
      </div>
      {hint && <p className={clsx("mt-1 text-xs", invalid ? "text-red-400" : darkMode ? "text-gray-500" : "text-gray-500")}>{hint}</p>}
    </div>
  )
}

/** Read-only figure, for values no endpoint can change (supply) or derived state. */
export const TokenReadout = ({ label, value, hint }: { label: string; value: string; hint?: string }) => {
  const { darkMode } = useProps()
  return (
    <div className={clsx("rounded-lg border px-4 py-3", darkMode ? "border-[rgba(255,255,255,0.08)] bg-[#060715]" : "border-gray-200 bg-gray-50")}>
      <p className={clsx("text-xs uppercase tracking-wide", darkMode ? "text-gray-500" : "text-gray-500")}>{label}</p>
      <p className={clsx("mt-1 font-mono text-lg", darkMode ? "text-white" : "text-gray-900")}>{value}</p>
      {hint && <p className={clsx("mt-1 text-xs", darkMode ? "text-gray-500" : "text-gray-500")}>{hint}</p>}
    </div>
  )
}
